import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import AdCard from "../components/AdCard";

// Google Maps imports
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";

// Category and gender lists
const categories = [
  "Arts & Culture",
  "Education & Skills",
  "Entertainment",
  "Family & Community",
  "Online & Virtual",
  "Pets & Animals",
  "Sports & Fitness",
  "Travel & Outdoor",
];
const genders = ["Anyone", "Male", "Female"];

// If you need Autocomplete or other features, add "places" to libraries
const libraries = [];

const Home = () => {
  // State to store all ads from Firestore
  const [allAds, setAllAds] = useState([]);
  // State to store filtered ads
  const [filteredAds, setFilteredAds] = useState([]);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");

  // New state for sorting
  const [sortBy, setSortBy] = useState("publishedDesc"); 
  // "publishedDesc" = newest first (default)

  // Map visibility state
  const [showMap, setShowMap] = useState(false);

  // State for the selected ad (for the InfoWindow)
  const [selectedAd, setSelectedAd] = useState(null);

  // Initial center and zoom for the map (San Francisco)
  const [mapCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [mapZoom] = useState(10);

  // Reference for the Google Map instance
  const mapRef = useRef(null);

  // Load Google Maps scripts
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  /**
   * Fetch all ads from Firestore and convert lat/lng to numbers if needed
   */
  const getAllAds = async () => {
    const adsRef = collection(db, "ads");
    const snapshot = await getDocs(adsRef);

    let adsArr = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Convert lat/lng to numbers if they exist and are not null
      if (data.lat !== undefined && data.lat !== null) {
        data.lat = Number(data.lat);
      }
      if (data.lng !== undefined && data.lng !== null) {
        data.lng = Number(data.lng);
      }
      adsArr.push(data);
    });

    setAllAds(adsArr);
    // Immediately filter (and sort) after loading
    filterAndSetAds(adsArr, mapRef.current, categoryFilter, genderFilter, showMap, sortBy);
  };

  // Fetch all ads once on component mount
  useEffect(() => {
    getAllAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever filters, map visibility, or sort option changes, re-filter and re-sort ads
  useEffect(() => {
    filterAndSetAds(allAds, mapRef.current, categoryFilter, genderFilter, showMap, sortBy);
  }, [categoryFilter, genderFilter, showMap, allAds, sortBy]);

  /**
   * Sort ads based on the user's selection
   */
  const sortAds = (adsArray, sortOption) => {
    // Make a copy of the array to avoid mutating the original
    const sorted = [...adsArray];

    switch (sortOption) {
      case "eventDateAsc":
        // Sort by event date: earliest first
        sorted.sort((a, b) => {
          if (!a.eventDate && !b.eventDate) return 0;
          if (!a.eventDate) return 1;
          if (!b.eventDate) return -1;
          return a.eventDate.toDate() - b.eventDate.toDate();
        });
        break;
      case "eventDateDesc":
        // Sort by event date: farthest first
        sorted.sort((a, b) => {
          if (!a.eventDate && !b.eventDate) return 0;
          if (!a.eventDate) return 1;
          if (!b.eventDate) return -1;
          return b.eventDate.toDate() - a.eventDate.toDate();
        });
        break;
      case "publishedAsc":
        // Sort by publish time: oldest first
        sorted.sort((a, b) => a.publishedAt.toDate() - b.publishedAt.toDate());
        break;
      case "publishedDesc":
      default:
        // Sort by publish time: newest first
        sorted.sort((a, b) => b.publishedAt.toDate() - a.publishedAt.toDate());
        break;
    }

    return sorted;
  };

  /**
   * Filter ads by category, gender, and map bounds (if map is shown)
   * Then sort the filtered ads
   */
  const filterAndSetAds = (all, mapObj, cat, gen, mapVisible, sortOption) => {
    // 1. Filter by category and gender
    let result = all.filter((ad) => {
      if (cat && ad.category !== cat) return false;
      if (gen && ad.gender !== gen) return false;
      return true;
    });

    // 2. If the map is hidden, do NOT filter by map bounds
    if (!mapVisible) {
      const sorted = sortAds(result, sortOption);
      setFilteredAds(sorted);
      return;
    }

    // 3. If the map is shown, we also filter by bounding box
    if (!mapObj) {
      // If map object is not ready yet, just sort
      const sorted = sortAds(result, sortOption);
      setFilteredAds(sorted);
      return;
    }

    const bounds = mapObj.getBounds();
    if (!bounds) {
      // If bounds are not ready, just sort
      const sorted = sortAds(result, sortOption);
      setFilteredAds(sorted);
      return;
    }

    const ne = bounds.getNorthEast(); // top-right
    const sw = bounds.getSouthWest(); // bottom-left

    // 4. Filter by coordinates
    result = result.filter((ad) => {
      // Check lat/lng are not null, undefined or NaN
      if (typeof ad.lat !== "number" || typeof ad.lng !== "number") return false;
      // Now check if they are in bounds
      const inLatBounds = ad.lat >= sw.lat() && ad.lat <= ne.lat();
      const inLngBounds = ad.lng >= sw.lng() && ad.lng <= ne.lng();
      return inLatBounds && inLngBounds;
    });

    // 5. Sort and set
    const sorted = sortAds(result, sortOption);
    setFilteredAds(sorted);
  };

  // Store the map instance in a ref on load
  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // This function is called when the map becomes idle (user stops dragging/zooming)
  const handleMapIdle = () => {
    if (!mapRef.current) return;
    filterAndSetAds(allAds, mapRef.current, categoryFilter, genderFilter, showMap, sortBy);
  };

  // If the map scripts haven't finished loading yet
  if (!isLoaded) {
    return <div>Loading map...</div>;
  }

  return (
    <div className="mt-5 container">
      {/* Button to toggle map visibility */}
      <div className="mb-4 text-center">
        <button 
          className="btn btn-primary"
          onClick={() => setShowMap(!showMap)}
        >
          {showMap ? "Hide Map" : "Show Map"}
        </button>
      </div>

      {/* Conditionally render the map only if showMap === true */}
      {showMap && (
        <div style={{ width: "100%", height: "400px", marginBottom: "1rem" }}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={onLoadMap}
            onIdle={handleMapIdle}
          >
            {/* Render markers for filtered ads */}
            {filteredAds.map((ad) => (
              <Marker
                key={ad.adId}
                position={{ lat: ad.lat, lng: ad.lng }}
                onClick={() => setSelectedAd(ad)} 
              />
            ))}

            {/* InfoWindow for the selected ad */}
            {selectedAd && (
              <InfoWindow
                position={{ lat: selectedAd.lat, lng: selectedAd.lng }}
                onCloseClick={() => setSelectedAd(null)}
              >
                <div style={{ maxWidth: "200px" }}>
                  {/* Use AdCard with hideImage to avoid displaying the image in the popup */}
                  <AdCard ad={selectedAd} hideImage />
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      )}

      {/* Filters + Sorting section */}
      <div className="d-flex justify-content-center justify-content-md-between align-items-center flex-wrap mb-5 form">
        {/* Filter by Category */}
        <div className="mb-3 mb-md-0">
          <h5>Find Someone ToGoWith For:</h5>
          <select
            className="form-select"
            style={{ width: "200px", margin: "auto" }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Filter by Gender */}
        <div className="mb-3 mb-md-0">
          <h5>Event For:</h5>
          <select
            className="form-select"
            style={{ width: "200px", margin: "auto" }}
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="">All</option>
            {genders.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        {/* Sort by dropdown */}
        <div className="mb-3 mb-md-0">
          <h5>Sort By</h5>
          <select
            className="form-select"
            style={{ width: "200px", margin: "auto" }}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="publishedDesc">Published (Newest First)</option>
            <option value="publishedAsc">Published (Oldest First)</option>
            <option value="eventDateAsc">Event Date (Soonest First)</option>
            <option value="eventDateDesc">Event Date (Farthest First)</option>
          </select>
        </div>
      </div>

      {/* List of filtered (and sorted) ads */}
      <h3>Recent Listings</h3>
      <div className="row">
        {filteredAds.map((ad) => (
          <div className="col-sm-6 col-md-4 col-xl-3 mb-3" key={ad.adId}>
            <AdCard ad={ad} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Home;
